import React, { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { FileDownloadOutlined, VisibilityOutlined } from '@mui/icons-material';
import { PdpScoreReportDialog } from '../../../../components/PdpScoreStatus/PdpScoreReportDialog';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstant from '../../../../api';
import {
  productConsumerVoiceItemEndpoint,
  productAiPresenceItemEndpoint,
  rmnProductCategorizeEndpoint,
} from '../../../../api/endpoints';
import { CvScoreChip } from '../../../../components/ConsumerVoice/CvScoreChip';
import { ConsumerVoiceReportDialog } from '../../../../components/ConsumerVoice/ConsumerVoiceReportDialog';
import { ConsumerVoiceRunDialog } from '../../../../components/ConsumerVoice/ConsumerVoiceRunDialog';
import { downloadConsumerVoicePdf } from '../../../../components/ConsumerVoice/consumerVoiceFiles';
import { AipScoreChip } from '../../../../components/AiPresence/AipScoreChip';
import { AiPresenceReportDialog } from '../../../../components/AiPresence/AiPresenceReportDialog';
import { AiPresenceRunDialog } from '../../../../components/AiPresence/AiPresenceRunDialog';
import { downloadAiPresencePdf } from '../../../../components/AiPresence/aiPresenceFiles';
import { ISingleProduct } from './constants';
import { ProductMenu } from './ProductMenu';
import { ProductRunReport } from './ProductRunReport';
import { ProductFilterKey, ProductFilters } from './ProductFilters';
import { IconPlus, IconProduct, IconRefresh, IconSparkles } from '../../../../utils/icons';
import { PRODUCT_MODAL_TAB, ProductV2Modal } from '../productModal';
import { RunningBadge } from '../../../../components/RunningBadge';
import { MainContainer } from '../../../../components/ContentWrapper';
import { ListPageHeader } from '../../../../components/ListPageHeader/ListPageHeader';
import {
  DataTable,
  DataTableColumn,
} from '../../../../components/DataTable/DataTable';
import { IdPill } from '../../../../components/IdPill/IdPill';
import { isInternal } from '../../../../utils';
import { downloadPdpScorePdf } from '../../../../components/PdpAudit/reportFiles';
import { useUser } from '../../../../hooks/useUser';
import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import { ScoreChip } from '../../../network/catalogShared';
import { CatalogTabs, PRODUCT_CATALOG_TABS } from '../../../network/CatalogTabs';
import { BetaPill } from '../../../../components/BetaPill';

interface IProductList {
  isLoading?: boolean;
  count: number;
  products: Array<ISingleProduct>;
  onClickNew: () => void;
  // Brand portal passes this to surface the crawl-import review page —
  // shown as Products | Import header tabs (the tabs navigate themselves;
  // the callback is kept for API compatibility but no longer invoked).
  onClickImport?: () => void;
  onUpdate?: () => void;
  onLoadMore?: () => void;
}

function CountCell({ value, title }: { value: number; title?: string }) {
  if (!value) return <span className="text-muted">—</span>;
  return (
    <span className="text-[12px] text-text-2" title={title}>
      {value}
    </span>
  );
}

// Brand-portal Products listing, on the same ListPageHeader + DataTable
// standard as the Catalog pages (Brands, import review).
export function ProductListingView({
  count = 0,
  isLoading,
  products,
  onClickNew,
  onClickImport,
  onUpdate,
  onLoadMore,
}: IProductList) {
  const { role } = useUser();
  const {
    brandId: accountBrandId,
    brandName,
    basePath,
    inWorkspace,
    permittedBrandIds,
    permittedCategoryIds,
  } = useNetworkScope();
  // Either allowlist (internal Brand > Team) makes URL product adds 403 —
  // the product's supplier/category is only known after import.
  const canAddProduct = permittedBrandIds === null && permittedCategoryIds === null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Latest audit opened in the viewer (PdpScoreReportDialog, the same one
  // the product's PDP URLs panel uses for its score history).
  const [viewingProduct, setViewingProduct] = useState<ISingleProduct | null>(null);
  const [reportProduct, setReportProduct] = useState<ISingleProduct | null>(
    null
  );
  // Search + filters live in the URL — the parent page refetches on any
  // searchParams change, so setting a param IS the fetch trigger.
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || ""
  );
  const productCategoryFilter = searchParams.get("product_category_id") || "";
  const rmnCategoryFilter = searchParams.get("rmn_category_id") || "";
  const brandFilter = searchParams.get("rmn_brand_id") || "";

  useEffect(() => {
    const t = setTimeout(() => {
      // Only when the typed search differs from the URL's. react-router 6.30
      // navigates on every setSearchParams, even one that returns `prev`, so
      // a no-op call pushed a duplicate list entry each time the list
      // mounted, and the browser's Back needed two presses to leave it.
      const current = new URLSearchParams(window.location.search).get("search") || "";
      if (current === searchInput) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if ((next.get("search") || "") === searchInput) return prev;
        if (searchInput) next.set("search", searchInput);
        else next.delete("search");
        return next;
      });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, setSearchParams]);

  const setFilter = (key: ProductFilterKey, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  const openProductDetail = (product: ISingleProduct) => {
    searchParams.set("productId", product.desireid.toString());
    setSearchParams(searchParams);
  };

  // Download the latest audit run's PDF for the product's network PDP.
  const [downloadingScoreIds, setDownloadingScoreIds] = useState<Set<number>>(
    new Set()
  );
  const handleDownloadLatest = async (p: ISingleProduct) => {
    const lr = p.latest_report;
    if (!lr || downloadingScoreIds.has(p.desireid)) return;
    setDownloadingScoreIds((prev) => new Set(prev).add(p.desireid));
    try {
      await downloadPdpScorePdf({
        productId: p.desireid,
        pdpUrlId: lr.pdp_url_id,
        scoreId: lr.score_id,
        url: lr.pdp_url,
      });
    } finally {
      setDownloadingScoreIds((prev) => {
        const next = new Set(prev);
        next.delete(p.desireid);
        return next;
      });
    }
  };

  // Consumer Voice (Run CV Analysis): one POST per product, stamped with the
  // product's own (supplier) brand — the same brand the row's PDP score is
  // scoped to. The row's cv_running flag comes back on refetch.
  const productBrandId = (p: ISingleProduct): number | null => {
    const brands: any[] = Array.isArray(p.brands) ? p.brands : [];
    const own = brands.find((b) => b && b.id !== accountBrandId) || brands[0];
    return own ? own.id : accountBrandId || null;
  };
  // Asks for subreddit hints first (ConsumerVoiceRunDialog), then starts.
  const [cvRunFor, setCvRunFor] = useState<ISingleProduct | null>(null);
  const handleRunConsumerVoice = (p: ISingleProduct) => {
    if (!productBrandId(p)) return;
    setCvRunFor(p);
  };
  const [viewingCvProduct, setViewingCvProduct] = useState<ISingleProduct | null>(null);
  // AI Presence: same brand stamping as Consumer Voice.
  const [aipRunFor, setAipRunFor] = useState<ISingleProduct | null>(null);
  const [viewingAipProduct, setViewingAipProduct] = useState<ISingleProduct | null>(null);
  const aipItemEndpoint = (p: ISingleProduct, auditId: number) =>
    productAiPresenceItemEndpoint(p.desireid, auditId, productBrandId(p));
  const cvItemEndpoint = (p: ISingleProduct, auditId: number) =>
    productConsumerVoiceItemEndpoint(p.desireid, auditId, productBrandId(p));
  const handleCreateStrategyFromCv = (p: ISingleProduct, auditId: number) => {
    const params = new URLSearchParams({
      desireid: String(p.desireid),
      ...(p.title ? { title: p.title } : {}),
      ...(p.image ? { image: p.image } : {}),
      cv: String(auditId),
    });
    const path = inWorkspace || !isInternal(role) ? `${basePath}/opportunities/strategies/new` : '/proposal';
    navigate(`${path}?${params.toString()}`);
  };
  const handleCreateStrategyFromAip = (p: ISingleProduct, auditId: number) => {
    const params = new URLSearchParams({
      desireid: String(p.desireid),
      ...(p.title ? { title: p.title } : {}),
      ...(p.image ? { image: p.image } : {}),
      aip: String(auditId),
    });
    const path = inWorkspace || !isInternal(role) ? `${basePath}/opportunities/strategies/new` : '/proposal';
    navigate(`${path}?${params.toString()}`);
  };

  // Autopilot: opens the product's settings modal straight on its Autopilot
  // tab (the list payload carries no recommendation counts yet).
  const [autopilotProduct, setAutopilotProduct] = useState<ISingleProduct | null>(null);

  // "Categorize" (products with no network category — they reached the
  // catalog outside the analysis, so nothing filed them): the API asks
  // Gemini for the best-fitting node of the account's own category tree and
  // records it; refetch so the column, filter and tree counts pick it up.
  const [categorizingIds, setCategorizingIds] = useState<Set<number>>(
    new Set()
  );
  const handleCategorize = async (p: ISingleProduct) => {
    if (categorizingIds.has(p.desireid)) return;
    setCategorizingIds((prev) => new Set(prev).add(p.desireid));
    try {
      const { data } = await axiosInstant.post(
        rmnProductCategorizeEndpoint(p.desireid)
      );
      if (data && data.categorized && data.rmn_category) {
        toast.success(`Filed under ${data.rmn_category.name}.`);
        onUpdate?.();
      } else {
        toast(
          `Couldn't find a ${brandName || "network"} category that fits this product.`
        );
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("You've reached the hourly limit — try again later.");
      } else if (status === 503) {
        toast.error("Categorization isn't available right now. Please try again.");
      } else {
        toast.error("Couldn't categorize this product. Please try again.");
      }
    } finally {
      setCategorizingIds((prev) => {
        const next = new Set(prev);
        next.delete(p.desireid);
        return next;
      });
    }
  };

  const columns: DataTableColumn<ISingleProduct>[] = [
    {
      key: "product",
      header: "Product",
      render: (p) => (
        <div className="flex items-center gap-[10px] min-w-0">
          <div className="w-[30px] h-[30px] rounded-[7px] bg-tint flex items-center justify-center shrink-0 overflow-hidden">
            {p.image ? (
              <img
                src={p.image}
                alt=""
                className="w-full h-full object-contain"
              />
            ) : (
              <IconProduct width={16} height={16} />
            )}
          </div>
          <Tooltip title={p.title || ""}>
            <span className="font-semibold text-text-1 truncate max-w-[340px]">
              {p.title}
            </span>
          </Tooltip>
          {isInternal(role) && <IdPill value={p.desireid} />}
        </div>
      ),
    },
    {
      key: "brand",
      header: "Brand",
      // The product's own brand. In a network catalog every product also
      // carries the account brand (the retailer), so prefer the OTHER one —
      // the supplier — and fall back to whatever is first. Same pill the
      // Discovered tab uses for its Brand column.
      render: (p) => {
        const brands: any[] = Array.isArray(p.brands) ? p.brands : [];
        const own = brands.find((b) => b && b.id !== accountBrandId) || brands[0];
        return own ? (
          <Tooltip title={own.name}>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1 max-w-[180px] truncate align-middle">
              {own.name}
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "category",
      header: "TDC Category",
      render: (p) => {
        const cat = p.product_categories?.[0];
        if (!cat || cat.name === "Uncategorized")
          return <span className="text-muted">—</span>;
        return (
          <Tooltip title={cat.name}>
            <span className="inline-block bg-surface-2 text-text-2 text-[11px] font-semibold rounded-full px-2.5 py-1 max-w-[180px] truncate align-middle">
              {cat.name}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: "rmn_category",
      // The account's own catalog category, e.g. "Sam's Club Category".
      // Empty → a Categorize button in its place (see handleCategorize).
      header: `${brandName || "Network"} Category`,
      render: (p) =>
        p.rmn_category ? (
          <Tooltip title={p.rmn_category.name}>
            <span className="inline-block bg-tint text-accent-text text-[11px] font-semibold rounded-full px-2.5 py-1 max-w-[180px] truncate align-middle">
              {p.rmn_category.name}
            </span>
          </Tooltip>
        ) : (
          <button
            type="button"
            title={`Find the ${brandName || "network"} category this product belongs in`}
            onClick={(e) => {
              e.stopPropagation();
              handleCategorize(p);
            }}
            disabled={categorizingIds.has(p.desireid)}
            className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {categorizingIds.has(p.desireid) ? "Categorizing…" : "Categorize"}
          </button>
        ),
    },
    {
      key: "score",
      header: "PDP Score",
      // Score chip plus the Run PDP Analysis icon (same refresh icon the
      // Catalog Brands / Categories pages use). Disabled only while a
      // report is in flight — a product with no PDP URLs gets a URL prompt
      // inside the launcher instead of a dead button. The latest audit's
      // date + view/download sit under the chip so the column reads as one
      // "PDP" block (the CV column mirrors it).
      render: (p) => (
        <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1.5">
            {typeof p.pdp_score === "number" ? (
              <ScoreChip
                score={p.pdp_score}
                title="Latest PDP score for this product (averaged when it has several PDPs)"
              />
            ) : (
              <span className="text-muted">—</span>
            )}
            {p.pdp_report_running ? (
              <RunningBadge
                label="Analyzing"
                title="A report is running for this product's page — the score updates when it finishes."
              />
            ) : (
              <Tooltip title="Run PDP Analysis — audit this product's page and roll the findings into its score">
                <button
                  type="button"
                  aria-label={`Run ${p.title} PDP analysis`}
                  onClick={() => setReportProduct(p)}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <IconRefresh size={13} />
                </button>
              </Tooltip>
            )}
          </div>
          {p.latest_report && (
            <div className="flex items-center gap-1">
              <span
                className="text-[11px] text-text-2 whitespace-nowrap"
                title={`Latest audit: ${dayjs(p.latest_report.scored_at).format("MMM DD, YYYY h:mm A")}`}
              >
                {dayjs(p.latest_report.scored_at).format("MMM DD, YYYY")}
              </span>
              <Tooltip title="View the latest audit report">
                <button
                  type="button"
                  aria-label={`View latest report for ${p.title}`}
                  onClick={() => setViewingProduct(p)}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <VisibilityOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
              <Tooltip title="Download the latest audit report (PDF)">
                <span>
                  <button
                    type="button"
                    aria-label={`Download latest report for ${p.title}`}
                    onClick={() => handleDownloadLatest(p)}
                    disabled={downloadingScoreIds.has(p.desireid)}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                  >
                    <FileDownloadOutlined sx={{ fontSize: 15 }} />
                  </button>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "cv_score",
      header: "CV Score",
      // Score chip plus the Run CV Analysis icon, with the latest analysis's
      // date + view/download underneath (same layout as the PDP column).
      render: (p) => {
        let score: React.ReactNode = <span className="text-muted">—</span>;
        if (typeof p.cv_score === "number") score = <CvScoreChip score={p.cv_score} subscores={p.cv_subscores} />;
        else if (p.cv_latest) {
          score = <span className="text-muted" title="Analyzed, but too little consumer discussion to score">n/a</span>;
        }
        return (
          <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-1.5">
              {score}
              {p.cv_running ? (
                <RunningBadge
                  label="Analyzing"
                  title="A Consumer Voice analysis is running for this product — the score updates when it finishes."
                />
              ) : (
                <Tooltip title="Run CV Analysis — read what shoppers say about this product on Reddit and score it">
                  <button
                    type="button"
                    aria-label={`Run ${p.title} CV analysis`}
                    onClick={() => handleRunConsumerVoice(p)}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                  >
                    <IconRefresh size={13} />
                  </button>
                </Tooltip>
              )}
            </div>
            {p.cv_latest && (
              <div className="flex items-center gap-1">
                <span
                  className="text-[11px] text-text-2 whitespace-nowrap"
                  title={`Latest Consumer Voice analysis: ${dayjs(p.cv_latest.scored_at).format("MMM DD, YYYY h:mm A")}`}
                >
                  {dayjs(p.cv_latest.scored_at).format("MMM DD, YYYY")}
                </span>
                <Tooltip title="View the latest Consumer Voice analysis">
                  <button
                    type="button"
                    aria-label={`View Consumer Voice analysis for ${p.title}`}
                    onClick={() => setViewingCvProduct(p)}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                  >
                    <VisibilityOutlined sx={{ fontSize: 15 }} />
                  </button>
                </Tooltip>
                <Tooltip title="Download the latest Consumer Voice analysis (PDF)">
                  <button
                    type="button"
                    aria-label={`Download Consumer Voice analysis for ${p.title}`}
                    onClick={() => {
                      if (p.cv_latest) downloadConsumerVoicePdf(cvItemEndpoint(p, p.cv_latest.id));
                    }}
                    className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                  >
                    <FileDownloadOutlined sx={{ fontSize: 15 }} />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "aip_score",
      header: "AI Presence",
      // Same layout as the CV column.
      render: (p) => (
        <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1.5">
            {typeof p.aip_score === "number" ? (
              <AipScoreChip score={p.aip_score} subscores={p.aip_subscores} />
            ) : (
              <span className="text-muted">—</span>
            )}
            {p.aip_running ? (
              <RunningBadge
                label="Analyzing"
                title="An AI Presence analysis is running for this product — the score updates when it finishes."
              />
            ) : (
              <Tooltip title="Run AI Presence — how often Claude, Gemini and OpenAI surface and recommend this product">
                <button
                  type="button"
                  aria-label={`Run ${p.title} AI Presence analysis`}
                  onClick={() => {
                    if (productBrandId(p)) setAipRunFor(p);
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <IconRefresh size={13} />
                </button>
              </Tooltip>
            )}
          </div>
          {p.aip_latest && (
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-text-2 whitespace-nowrap">{dayjs(p.aip_latest.scored_at).format("MMM DD, YYYY")}</span>
              <Tooltip title="View the latest AI Presence analysis">
                <button
                  type="button"
                  aria-label={`View AI Presence analysis for ${p.title}`}
                  onClick={() => setViewingAipProduct(p)}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <VisibilityOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
              <Tooltip title="Download the latest AI Presence analysis (PDF)">
                <button
                  type="button"
                  aria-label={`Download AI Presence analysis for ${p.title}`}
                  onClick={() => {
                    if (p.aip_latest) downloadAiPresencePdf(aipItemEndpoint(p, p.aip_latest.id));
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
                >
                  <FileDownloadOutlined sx={{ fontSize: 15 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "videos",
      header: "Videos",
      render: (p) => <CountCell value={(p.productVideos || []).length} />,
    },
    {
      key: "pdps",
      header: "PDP URLs",
      render: (p) => (
        <CountCell
          value={p.pdp_url_count || 0}
          title="Product pages (PDP URLs)"
        />
      ),
    },
    // Autopilot is internal-only for now.
    ...(isInternal(role)
      ? [
        {
          key: 'autopilot',
          header: (
            <span className="inline-flex items-center gap-1.5">
              Autopilot <BetaPill />
            </span>
          ),
          render: (p: ISingleProduct) => (
            <div onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Autopilot — what to do next on this product's pages and videos: observations, tracked changes, recommendations, and measurement">
                <button
                  type="button"
                  aria-label={`Open Autopilot for ${p.title}`}
                  onClick={() => setAutopilotProduct(p)}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-2 shrink-0"
                >
                  <IconSparkles size={15} />
                </button>
              </Tooltip>
            </div>
          ),
        },
        ]
      : []),
    {
      // Strategies start from each analysis's report (PDP, Consumer Voice,
      // AI Presence), so the row only carries the menu.
      key: "actions",
      header: "",
      render: (p) => (
        <div
          className="flex items-center justify-end gap-1 whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
        >
          <ProductMenu product={p} onUpdate={onUpdate} />
        </div>
      ),
    },
  ];

  return (
    <MainContainer>
      <ListPageHeader
        title="Products"
        subtitle={
          count > 0
            ? `Products in your catalog · ${count} total`
            : "Products in your catalog"
        }
        actionLabel={canAddProduct ? "Add product" : undefined}
        onActionClick={canAddProduct ? onClickNew : undefined}
        actionIcon={<IconPlus width={16} height={16} stroke="#fff" />}
        tabs={onClickImport ? <CatalogTabs tabs={PRODUCT_CATALOG_TABS} /> : undefined}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchInputId="search-products"
        searchPlaceholder="Search products…"
        searchRightContent={
          <ProductFilters
            productCategoryId={productCategoryFilter}
            brandId={brandFilter}
            rmnCategoryId={rmnCategoryFilter}
            onChange={setFilter}
          />
        }
      />

      <DataTable<ISingleProduct>
        columns={columns}
        rows={products}
        loading={!!isLoading}
        totalCount={count}
        hasMore={products.length < count}
        onLoadMore={() => onLoadMore?.()}
        emptyMessage={
          searchInput || productCategoryFilter || rmnCategoryFilter || brandFilter
            ? "No products match your search or filters"
            : "No products yet — use Add product to get started"
        }
        getRowKey={(p) => p.desireid}
        onRowClick={openProductDetail}
      />

      {viewingProduct && viewingProduct.latest_report && (
        <PdpScoreReportDialog
          productId={viewingProduct.desireid}
          pdpUrlId={viewingProduct.latest_report.pdp_url_id}
          scoreId={viewingProduct.latest_report.score_id}
          pdpUrl={viewingProduct.latest_report.pdp_url}
          onClose={() => setViewingProduct(null)}
        />
      )}
      {viewingCvProduct && viewingCvProduct.cv_latest && (
        <ConsumerVoiceReportDialog
          endpoint={cvItemEndpoint(viewingCvProduct, viewingCvProduct.cv_latest.id)}
          onClose={() => setViewingCvProduct(null)}
          onCreateStrategy={(audit) => handleCreateStrategyFromCv(viewingCvProduct, audit.id)}
          createStrategyLabel="Create strategy"
        />
      )}
      {viewingAipProduct && viewingAipProduct.aip_latest && (
        <AiPresenceReportDialog
          endpoint={aipItemEndpoint(viewingAipProduct, viewingAipProduct.aip_latest.id)}
          onClose={() => setViewingAipProduct(null)}
          onCreateStrategy={(audit) => handleCreateStrategyFromAip(viewingAipProduct, audit.id)}
        />
      )}
      {aipRunFor && (
        <AiPresenceRunDialog
          desireid={aipRunFor.desireid}
          brandId={productBrandId(aipRunFor) as number}
          productTitle={aipRunFor.title}
          onClose={() => setAipRunFor(null)}
          onStarted={() => onUpdate?.()}
        />
      )}
      {cvRunFor && (
        <ConsumerVoiceRunDialog
          desireid={cvRunFor.desireid}
          brandId={productBrandId(cvRunFor) as number}
          productTitle={cvRunFor.title}
          onClose={() => setCvRunFor(null)}
          onStarted={() => onUpdate?.()}
        />
      )}
      {autopilotProduct && (
        <ProductV2Modal
          product={autopilotProduct}
          initialTab={PRODUCT_MODAL_TAB.autopilot}
          onClose={() => setAutopilotProduct(null)}
          onComplete={() => {
            setAutopilotProduct(null);
            onUpdate?.();
          }}
        />
      )}
      {reportProduct && (
        <ProductRunReport
          product={reportProduct}
          onClose={() => setReportProduct(null)}
          // Refetch so the row's "Report running…" state lights up.
          onStarted={() => onUpdate?.()}
        />
      )}
    </MainContainer>
  );
}
